(function () {
  "use strict";

  /**
   * Candle Sand Calculator — multi-market edition
   *
   * What changed vs. the previous version:
   * - Prices are NO LONGER hardcoded in EUR. They are fetched live from Shopify
   *   via the locale-aware Ajax API, so each visitor sees the correct price for
   *   their market (including manual per-market price overrides, not just
   *   currency conversion).
   * - Currency is read from Shopify's active currency, so the symbol/format
   *   always matches the rest of the store.
   * - The hardcoded prices below are kept ONLY as a fallback if a fetch fails
   *   or the script runs off-Shopify (e.g. local testing).
   */

  const DEBUG = false;
  const BULK_ORDER_THRESHOLD_KG = 100;
  const WEIGHT_STEP_GRAMS = 50;

  // Shopify globals (available on every storefront). Safe fallbacks off-Shopify.
  const SHOPIFY_ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  let ACTIVE_CURRENCY =
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "EUR";

  // fallbackPrice + fallbackCurrency are ONLY used if the live fetch fails.
  const FALLBACK_CURRENCY = "EUR";

  // Email address that bulk/wholesale enquiries are sent to.
  // >>> UPDATE THIS to your real contact address. <<<
  const CONTACT_EMAIL = "hello@candleracandle.eu";
  const MAX_VASES = 200;

  const PRODUCTS = [
    {
      id: 1,
      handle: "candle-sand-30-wicks-by-candlera%C2%AE",
      name: "0.65kg Candle Sand + 20 Wicks",
      weightKg: 0.65,
      price: 25, // live value overwrites this; EUR fallback only
      img: "https://cdn.shopify.com/s/files/1/0807/0017/8778/files/32il_fullxfull_5771028690_cu80_jpg1-ezgif.com-webp-to-jpg-converter.jpg?v=1738766704",
      url: "/collections/candle-sand/products/candle-sand-30-wicks-by-candlera%C2%AE",
      rating: 5.0,
      features: ["Perfect for small vases", "20 premium cotton wicks", "Elegant packaging"]
    },
    {
      id: 2,
      handle: "candle-sand-3kg-6-6-lb-bucket-75-wicks-by-candlera%C2%AE",
      name: "3kg Candle Sand Bucket + 75 Wicks",
      weightKg: 3,
      price: 75,
      img: "https://candleracandle.eu/cdn/shop/files/Candlera-1-4.jpg",
      url: "/collections/candle-sand/products/candle-sand-3kg-6-6-lb-bucket-75-wicks-by-candlera%C2%AE",
      rating: 5.0,
      features: ["Most popular size", "Fill multiple vases", "75 premium cotton wicks", "Convenient storage bucket"]
    },
    {
      id: 3,
      handle: "candle-sand-4-5-kg-10-lb-bucket-150-wicks-by-candlera%C2%AE",
      name: "4.5kg Candle Sand Bucket + 150 Wicks",
      weightKg: 4.5,
      price: 89,
      img: "https://candleracandle.eu/cdn/shop/files/Candlera-2-4.jpg",
      url: "/collections/candle-sand/products/candle-sand-4-5-kg-10-lb-bucket-150-wicks-by-candlera%C2%AE",
      rating: 5.0,
      features: ["Great value pack", "150 premium cotton wicks", "Convenient storage bucket"]
    },
    {
      id: 4,
      handle: "95kg21-lb-candle-sand-bulk-bucket-250-41488",
      name: "9.5kg Candle Sand Bucket + 250 Wicks",
      weightKg: 9.5,
      price: 139,
      img: "https://candleracandle.eu/cdn/shop/products/il_fullxfull.5794094456_ivaz.jpg?v=1710671049&width=1400",
      url: "/collections/candle-sand/products/95kg21-lb-candle-sand-bulk-bucket-250-41488",
      rating: 5.0,
      features: ["Bulk size for large projects", "250 premium cotton wicks", "Best value per kg"]
    }
  ];

  // Runtime pricing state.
  let pricingLoaded = false;
  let lastSandKg = null; // remembers the last calc so we can re-render once prices arrive
  let lastElements = null;

  function log(...args) {
    if (DEBUG) console.log("Candle Calculator:", ...args);
  }

  function warn(...args) {
    if (DEBUG) console.warn("Candle Calculator:", ...args);
  }

  function formatMoney(amountNumber) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: ACTIVE_CURRENCY
      }).format(amountNumber);
    } catch (e) {
      return `${ACTIVE_CURRENCY} ${Number(amountNumber).toFixed(2)}`;
    }
  }

  /**
   * Fetch the live, market-correct price for one product.
   * Shopify's Ajax API returns `price` in the presentment currency's minor units
   * (e.g. cents) when called through the locale-aware root URL.
   * Works for 2-decimal currencies (EUR/GBP/USD, i.e. all of your markets).
   */
  async function fetchLivePrice(product) {
    const endpoint = `${SHOPIFY_ROOT}products/${product.handle}.js`;
    const res = await fetch(endpoint, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    });
    if (!res.ok) throw new Error(`HTTP ${res.status} for ${product.handle}`);
    const data = await res.json();
    if (typeof data.price !== "number") throw new Error(`No price for ${product.handle}`);
    return data.price / 100; // minor units -> major units
  }

  async function loadLivePricing() {
    if (!window.Shopify) {
      warn("Not on Shopify — using fallback EUR prices.");
      ACTIVE_CURRENCY = FALLBACK_CURRENCY;
      pricingLoaded = true;
      return;
    }

    const results = await Promise.allSettled(PRODUCTS.map(fetchLivePrice));

    let anySuccess = false;
    results.forEach((result, index) => {
      if (result.status === "fulfilled" && Number.isFinite(result.value)) {
        PRODUCTS[index].price = result.value;
        anySuccess = true;
      } else {
        warn("Live price fetch failed for", PRODUCTS[index].handle, result.reason);
      }
    });

    // If every fetch failed, the safest display currency is the EUR fallback,
    // because the fallback numbers are EUR.
    if (!anySuccess) {
      ACTIVE_CURRENCY = FALLBACK_CURRENCY;
    }

    pricingLoaded = true;
    log("Live pricing loaded. Currency:", ACTIVE_CURRENCY);

    // If the visitor already ran a calculation before prices arrived, refresh it.
    if (lastSandKg != null && lastElements) {
      const recommendation = findBestFitBundle(lastSandKg);
      displayProductRecommendation(recommendation, lastElements);
    }
  }

  function parseNumber(value) {
    return parseFloat(String(value).trim().replace(",", "."));
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function escapeAttribute(value) {
    return escapeHtml(value);
  }

  function validateInput(inputElement, errorElement, options) {
    const value = parseNumber(inputElement.value);
    const fieldName = options.fieldName;
    const min = options.min;
    const max = options.max;
    const integerOnly = Boolean(options.integerOnly);

    let errorMessage = "";

    if (String(inputElement.value).trim() === "") {
      errorMessage = `${fieldName} is required.`;
    } else if (Number.isNaN(value)) {
      errorMessage = `${fieldName} must be a valid number.`;
    } else if (integerOnly && !Number.isInteger(value)) {
      errorMessage = `${fieldName} must be a whole number.`;
    } else if (value < min || value > max) {
      errorMessage = `${fieldName} must be between ${min} and ${max}.`;
    }

    if (errorMessage) {
      errorElement.textContent = errorMessage;
      inputElement.classList.add("input-error-state");
      errorElement.classList.add("show");
      return false;
    }

    inputElement.classList.remove("input-error-state");
    errorElement.classList.remove("show");
    errorElement.textContent = "";
    return true;
  }

  function buildStarsHtml(rating) {
    const safeRating = Math.max(0, Math.min(5, Number(rating) || 0));
    const fullStars = Math.floor(safeRating);
    const halfStar = safeRating % 1 >= 0.5;
    let starsHtml = "";

    for (let i = 0; i < 5; i += 1) {
      if (i < fullStars) {
        starsHtml += '<i class="las la-star"></i>';
      } else if (i === fullStars && halfStar) {
        starsHtml += '<i class="las la-star-half-alt"></i>';
      } else {
        starsHtml += '<i class="lar la-star"></i>';
      }
    }

    return starsHtml;
  }

  function getProductWeightUnits(product) {
    return Math.round((product.weightKg * 1000) / WEIGHT_STEP_GRAMS);
  }

  function findBestFitBundle(requiredWeightKg) {
    if (!Number.isFinite(requiredWeightKg) || requiredWeightKg <= 0 || PRODUCTS.length === 0) {
      return null;
    }

    if (requiredWeightKg > BULK_ORDER_THRESHOLD_KG) {
      return {
        type: "bulk",
        requiredWeightKg
      };
    }

    const requiredUnits = Math.ceil((requiredWeightKg * 1000) / WEIGHT_STEP_GRAMS);
    const productUnits = PRODUCTS.map(getProductWeightUnits);
    const maxProductUnits = Math.max(...productUnits);
    const maxCapacityUnits = requiredUnits + maxProductUnits - 1;

    const costs = new Array(maxCapacityUnits + 1).fill(Infinity);
    const itemCounts = new Array(maxCapacityUnits + 1).fill(Infinity);
    const previousCapacity = new Array(maxCapacityUnits + 1).fill(-1);
    const previousProductIndex = new Array(maxCapacityUnits + 1).fill(-1);

    costs[0] = 0;
    itemCounts[0] = 0;

    for (let capacity = 1; capacity <= maxCapacityUnits; capacity += 1) {
      for (let productIndex = 0; productIndex < PRODUCTS.length; productIndex += 1) {
        const weightUnits = productUnits[productIndex];
        const remainingCapacity = capacity - weightUnits;

        if (remainingCapacity < 0 || costs[remainingCapacity] === Infinity) continue;

        const candidateCost = costs[remainingCapacity] + PRODUCTS[productIndex].price;
        const candidateItemCount = itemCounts[remainingCapacity] + 1;
        const isCheaper = candidateCost < costs[capacity];
        const isSamePriceButFewerItems =
          candidateCost === costs[capacity] && candidateItemCount < itemCounts[capacity];

        if (isCheaper || isSamePriceButFewerItems) {
          costs[capacity] = candidateCost;
          itemCounts[capacity] = candidateItemCount;
          previousCapacity[capacity] = remainingCapacity;
          previousProductIndex[capacity] = productIndex;
        }
      }
    }

    let bestCapacity = -1;

    for (let capacity = requiredUnits; capacity <= maxCapacityUnits; capacity += 1) {
      if (costs[capacity] === Infinity) continue;

      if (bestCapacity === -1) {
        bestCapacity = capacity;
        continue;
      }

      const isCheaper = costs[capacity] < costs[bestCapacity];
      const isSamePriceLessOverfill =
        costs[capacity] === costs[bestCapacity] && capacity < bestCapacity;
      const isSamePriceSameOverfillFewerItems =
        costs[capacity] === costs[bestCapacity] &&
        capacity === bestCapacity &&
        itemCounts[capacity] < itemCounts[bestCapacity];

      if (isCheaper || isSamePriceLessOverfill || isSamePriceSameOverfillFewerItems) {
        bestCapacity = capacity;
      }
    }

    if (bestCapacity === -1) {
      warn("No bundle found for", requiredWeightKg);
      return null;
    }

    const countsByProductId = new Map();
    let cursor = bestCapacity;

    while (cursor > 0 && previousProductIndex[cursor] !== -1) {
      const product = PRODUCTS[previousProductIndex[cursor]];
      countsByProductId.set(product.id, (countsByProductId.get(product.id) || 0) + 1);
      cursor = previousCapacity[cursor];
    }

    const items = PRODUCTS
      .map((product) => ({
        product,
        quantity: countsByProductId.get(product.id) || 0
      }))
      .filter((item) => item.quantity > 0)
      .sort((a, b) => b.product.weightKg - a.product.weightKg);

    const totalCost = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
    const totalWeightKg = items.reduce(
      (sum, item) => sum + item.product.weightKg * item.quantity,
      0
    );

    return {
      type: items.length > 1 ? "bundle" : "single",
      items,
      totalCost,
      totalWeightKg,
      overfillKg: Math.max(0, totalWeightKg - requiredWeightKg)
    };
  }

  function displayProductRecommendation(recommendation, elements) {
    const { productCardEl, totalCostEl } = elements;

    productCardEl.innerHTML = "";

    if (!recommendation) {
      totalCostEl.textContent = "N/A";
      productCardEl.innerHTML =
        '<p style="padding: 20px; text-align: center; color: #777;">Please enter your vase details to see a recommendation.</p>';
      return;
    }

    if (recommendation.type === "bulk") {
      totalCostEl.textContent = "Wholesale";

      const estimatedKg = recommendation.requiredWeightKg.toFixed(2);
      const subject = "Wholesale / Bulk Candle Sand Enquiry";
      const body =
        `Hi Candlera team,\n\n` +
        `I'd like a wholesale/bulk quote for candle sand.\n\n` +
        `Estimated amount needed: ~${estimatedKg} kg\n` +
        `Number of vases: \n` +
        `Country / delivery address: \n` +
        `Preferred timeline: \n` +
        `Company / event (optional): \n\n` +
        `Thank you!`;
      const mailtoHref =
        `mailto:${CONTACT_EMAIL}` +
        `?subject=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(body)}`;

      productCardEl.innerHTML = `
        <div class="best-fit-badge-ui">Bulk Order</div>
        <div class="product-details-content" style="padding: 20px;">
          <div class="product-name-text">Large Project / Wholesale Request</div>
          <div class="product-price-info">
            <div class="product-price-value">Estimated need: ${estimatedKg} kg</div>
          </div>
          <div class="product-features-list">
            <div class="feature-list-item"><i class="las la-check"></i><span>For orders above ${BULK_ORDER_THRESHOLD_KG} kg, wholesale pricing is usually more suitable.</span></div>
            <div class="feature-list-item"><i class="las la-check"></i><span>Tap below and we'll send you the best bulk price — the email is pre-filled, just add your details.</span></div>
          </div>
          <a href="${escapeAttribute(mailtoHref)}" class="buy-now-action-btn" style="display:inline-block;margin-top:12px;">Request Wholesale Pricing</a>
        </div>
      `;
      return;
    }

    const isBundle = recommendation.items.length > 1;

    totalCostEl.textContent = formatMoney(recommendation.totalCost);

    if (isBundle) {
      displayBundleRecommendation(recommendation, productCardEl);
      return;
    }

    // --- Single product ---
    const item = recommendation.items[0];
    const product = item.product;
    const starsHtml = buildStarsHtml(product.rating);

    const quantityText =
      item.quantity > 1
        ? `<div class="product-quantity-text">Quantity: ${item.quantity}</div>`
        : "";

    const featureHtml = product.features
      .map(
        (feature) =>
          `<div class="feature-list-item"><i class="las la-check"></i><span>${escapeHtml(feature)}</span></div>`
      )
      .join("");

    productCardEl.innerHTML = `
      <div class="best-fit-badge-ui">Best Fit</div>
      <div class="product-image-wrapper">
        <img src="${escapeAttribute(product.img)}" alt="${escapeAttribute(product.name)}" class="product-image-actual" loading="lazy">
      </div>
      <div class="product-details-content">
        <div class="product-name-text">${escapeHtml(product.name)}</div>
        <div class="product-price-info">
          <div class="product-price-value">${formatMoney(recommendation.totalCost)} total</div>
          ${quantityText}
        </div>
        <div class="product-rating-display">
          <div class="rating-stars-container">${starsHtml}</div>
          <div class="rating-value-text">${Number(product.rating).toFixed(1)} / 5</div>
        </div>
        <div class="product-features-list">${featureHtml}</div>
        <a href="${escapeAttribute(product.url)}" class="buy-now-action-btn">View Product</a>
      </div>
    `;
  }

  /**
   * Renders a bundle as a header + one self-contained card per product,
   * each with its own image, quantity, line price, and View Product button.
   * Styling (typography, layout, button hover) lives in injectCalculatorStyles().
   */
  function displayBundleRecommendation(recommendation, productCardEl) {
    const itemsHtml = recommendation.items
      .map(({ product, quantity }) => {
        const lineTotal = product.price * quantity;
        return `
          <div class="bundle-item-card">
            <div class="bundle-item-thumb">
              <img src="${escapeAttribute(product.img)}" alt="${escapeAttribute(product.name)}" loading="lazy">
            </div>
            <div class="bundle-item-info">
              <div class="bundle-item-heading">
                <span class="bundle-item-qty-chip">${quantity}×</span>
                <span class="bundle-item-title">${escapeHtml(product.name)}</span>
              </div>
              <div class="bundle-item-price">${formatMoney(lineTotal)}</div>
              <a href="${escapeAttribute(product.url)}" class="buy-now-action-btn">View Product</a>
            </div>
          </div>
        `;
      })
      .join("");

    productCardEl.innerHTML = `
      <div class="best-fit-badge-ui">Best Fit</div>
      <div class="product-details-content">
        <div class="product-name-text">Recommended Candle Sand Bundle</div>
        <div class="product-price-info">
          <div class="product-price-value">${formatMoney(recommendation.totalCost)} total</div>
          <div class="product-quantity-text">Total weight: ${recommendation.totalWeightKg.toFixed(2)} kg</div>
        </div>
        <div class="bundle-items-list">
          ${itemsHtml}
        </div>
      </div>
    `;
  }

  /**
   * Injects scoped styles for the recommendation area:
   * - Fixes the CTA button hover (theme hover was hiding the label).
   * - Sets consistent bundle-card typography and spacing.
   * Change --calc-gold / --calc-gold-dark to match your exact brand gold.
   */
  function injectCalculatorStyles() {
    if (document.getElementById("candleCalcStyles")) return;

    const style = document.createElement("style");
    style.id = "candleCalcStyles";
    style.textContent = `
      #candleSandCalculatorApp { --calc-gold:#b8955a; --calc-gold-dark:#a17f47; }

      /* Keep every calculator CTA readable on hover/focus */
      #candleSandCalculatorApp .buy-now-action-btn {
        background: var(--calc-gold);
        color: #fff !important;
        border: none;
        transition: background .15s ease;
      }
      #candleSandCalculatorApp .buy-now-action-btn:hover,
      #candleSandCalculatorApp .buy-now-action-btn:focus {
        background: var(--calc-gold-dark) !important;
        color: #fff !important;
        opacity: 1;
      }

      /* Bundle item cards */
      #candleSandCalculatorApp .bundle-items-list {
        display: flex; flex-direction: column; gap: 14px; margin-top: 16px;
      }
      #candleSandCalculatorApp .bundle-item-card {
        display: flex; gap: 16px; align-items: center;
        border: 1px solid #ececec; border-radius: 12px; padding: 14px; background: #fff;
      }
      #candleSandCalculatorApp .bundle-item-thumb {
        flex: 0 0 88px; width: 88px; height: 88px;
        border-radius: 10px; overflow: hidden; background: #f5f5f5;
      }
      #candleSandCalculatorApp .bundle-item-thumb img {
        width: 100%; height: 100%; object-fit: cover; display: block;
      }
      #candleSandCalculatorApp .bundle-item-info { flex: 1; min-width: 0; }
      #candleSandCalculatorApp .bundle-item-heading {
        display: flex; align-items: center; gap: 9px; margin: 0 0 8px;
      }
      #candleSandCalculatorApp .bundle-item-qty-chip {
        flex: 0 0 auto;
        background: var(--calc-gold); color: #fff;
        font-weight: 800; font-size: 1rem; line-height: 1;
        padding: 6px 10px; border-radius: 8px; white-space: nowrap;
      }
      #candleSandCalculatorApp .bundle-item-title {
        font-size: 1rem; line-height: 1.3; font-weight: 600; color: #1f1f1f;
      }
      #candleSandCalculatorApp .bundle-item-price {
        font-size: 1.05rem; font-weight: 700; color: var(--calc-gold); margin: 0 0 12px;
      }
      #candleSandCalculatorApp .bundle-item-card .buy-now-action-btn {
        display: flex; align-items: center; justify-content: center;
        width: 100%; box-sizing: border-box;
        padding: 12px 18px; font-size: .8rem; font-weight: 700;
        text-transform: uppercase; letter-spacing: .08em;
        border-radius: 8px; text-align: center;
      }
      @media (max-width: 600px) {
        #candleSandCalculatorApp .bundle-item-card { align-items: flex-start; gap: 12px; }
        #candleSandCalculatorApp .bundle-item-thumb { flex-basis: 64px; width: 64px; height: 64px; }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Removes the standalone "Why Candlera Candle Sand?" content block that sits
   * below the calculator. It's separate page content (not part of this widget),
   * so we locate it by its heading + a bullet phrase and strip the smallest
   * wrapper that contains both — while refusing to touch the calculator itself.
   */
  function removeWhyBlock() {
    const HEADING = "why candlera candle sand";
    const MARKER = "burns cleaner";

    const candidates = Array.from(
      document.querySelectorAll("h1,h2,h3,h4,h5,h6,p,strong,span,div")
    );
    const heading = candidates.find((el) =>
      (el.textContent || "").trim().toLowerCase().startsWith(HEADING)
    );
    if (!heading) return;

    // Walk up to the tightest ancestor that also contains the bullet text.
    let node = heading;
    let target = null;
    while (node && node !== document.body) {
      if ((node.textContent || "").toLowerCase().includes(MARKER)) {
        target = node;
        break;
      }
      node = node.parentElement;
    }

    const calculator = document.getElementById("candleSandCalculatorApp");
    const safeToRemove =
      target &&
      target !== document.body &&
      target.parentElement &&
      !(calculator && target.contains(calculator));

    if (safeToRemove) {
      target.remove();
      return;
    }

    // Fallback: remove the heading and following siblings up to the next heading.
    let sib = heading.nextElementSibling;
    heading.remove();
    while (sib && !/^H[1-6]$/.test(sib.tagName)) {
      const next = sib.nextElementSibling;
      sib.remove();
      sib = next;
    }
  }

  function initCandleCalculator() {
    const appContainer = document.getElementById("candleSandCalculatorApp");

    if (!appContainer) {
      log("App container not found on this page.");
      return;
    }

    if (appContainer.dataset.candleCalculatorInitialized === "true") {
      log("Already initialized. Skipping duplicate init.");
      return;
    }

    const elements = {
      calculateBtn: appContainer.querySelector("#calculateBtnAction"),
      resultValueEl: appContainer.querySelector("#calculatedResultValue"),
      productRecommendationsEl: appContainer.querySelector("#productRecommendationSection"),
      totalCostEl: appContainer.querySelector("#totalCostValueDisplay"),
      productCardEl: appContainer.querySelector("#recommendedProductCard"),
      bulkOrderNoticeEl: appContainer.querySelector("#bulkOrderInfoNotice"),
      numVasesInput: appContainer.querySelector("#numVases"),
      vaseDiameterInput: appContainer.querySelector("#vaseDiameter"),
      fillHeightInput: appContainer.querySelector("#fillHeight"),
      diameterUnitSelect: appContainer.querySelector("#diameterUnit"),
      heightUnitSelect: appContainer.querySelector("#heightUnit"),
      weightUnitOutputSelect: appContainer.querySelector("#weightUnitOutput"),
      vaseCountErrorEl: appContainer.querySelector("#vaseCountError"),
      vaseDiameterErrorEl: appContainer.querySelector("#vaseDiameterError"),
      fillHeightErrorEl: appContainer.querySelector("#fillHeightError")
    };

    const missingElements = Object.entries(elements)
      .filter(([, element]) => !element)
      .map(([name]) => name);

    if (missingElements.length > 0) {
      console.error(
        "Candle Calculator Error: Missing required DOM elements:",
        missingElements.join(", ")
      );

      if (elements.calculateBtn) {
        elements.calculateBtn.textContent = "Calculator Error";
        elements.calculateBtn.disabled = true;
      }

      return;
    }

    appContainer.dataset.candleCalculatorInitialized = "true";
    lastElements = elements;

    injectCalculatorStyles();
    removeWhyBlock();

    // Kick off live pricing immediately, so prices are ready before the user clicks.
    loadLivePricing().catch((e) => warn("loadLivePricing error:", e));

    const inputErrorPairs = [
      { input: elements.numVasesInput, error: elements.vaseCountErrorEl },
      { input: elements.vaseDiameterInput, error: elements.vaseDiameterErrorEl },
      { input: elements.fillHeightInput, error: elements.fillHeightErrorEl }
    ];

    inputErrorPairs.forEach(({ input, error }) => {
      input.addEventListener("input", () => {
        input.classList.remove("input-error-state");
        error.classList.remove("show");
        error.textContent = "";
        elements.resultValueEl.classList.remove("error-message-display");
      });
    });

    function calculateSandNeeded(event) {
      if (event) event.preventDefault();

      let allValid = true;

      allValid =
        validateInput(elements.numVasesInput, elements.vaseCountErrorEl, {
          min: 1,
          max: MAX_VASES,
          fieldName: "Number of Vases",
          integerOnly: true
        }) && allValid;

      allValid =
        validateInput(elements.vaseDiameterInput, elements.vaseDiameterErrorEl, {
          min: 0.1,
          max: 1000,
          fieldName: "Vase Diameter"
        }) && allValid;

      allValid =
        validateInput(elements.fillHeightInput, elements.fillHeightErrorEl, {
          min: 0.1,
          max: 1000,
          fieldName: "Desired Fill Height"
        }) && allValid;

      if (!allValid) {
        elements.resultValueEl.textContent = "Please correct errors.";
        elements.resultValueEl.classList.add("error-message-display");
        elements.productRecommendationsEl.classList.remove("show");
        return;
      }

      elements.resultValueEl.classList.remove("error-message-display");

      const numVases = parseNumber(elements.numVasesInput.value);
      let diameter = parseNumber(elements.vaseDiameterInput.value);
      let height = parseNumber(elements.fillHeightInput.value);

      const diameterUnit = elements.diameterUnitSelect.value;
      const heightUnit = elements.heightUnitSelect.value;
      const weightUnit = elements.weightUnitOutputSelect.value;

      if (diameterUnit === "in") diameter *= 2.54;
      if (heightUnit === "in") height *= 2.54;

      const radius = diameter / 2;
      const volumeCm3 = Math.PI * Math.pow(radius, 2) * height * numVases;

      // Candle sand density estimate: 0.5g / cm³ = 0.0005kg / cm³.
      const sandKg = volumeCm3 * 0.0005;
      const displayWeight = weightUnit === "lb" ? sandKg * 2.20462 : sandKg;
      const displayUnitLabel = weightUnit === "lb" ? "lb" : "kg";

      elements.resultValueEl.textContent = `${displayWeight.toFixed(2)} ${displayUnitLabel}`;

      lastSandKg = sandKg; // remember for re-render if prices load later

      const recommendation = findBestFitBundle(sandKg);
      displayProductRecommendation(recommendation, elements);

      elements.bulkOrderNoticeEl.style.display =
        sandKg > BULK_ORDER_THRESHOLD_KG ? "block" : "none";
      elements.productRecommendationsEl.classList.add("show");
    }

    elements.calculateBtn.addEventListener("click", calculateSandNeeded);
    displayProductRecommendation(null, elements);
    log("Initialized successfully.");
  }

  function runWhenReady() {
    if (document.readyState === "complete" || document.readyState === "interactive") {
      initCandleCalculator();
    } else {
      document.addEventListener("DOMContentLoaded", initCandleCalculator, { once: true });
    }
  }

  runWhenReady();

  // Helps in Shopify Theme Editor when sections are dynamically reloaded.
  document.addEventListener("shopify:section:load", initCandleCalculator);
})();